import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { environment } from '@environment';
import { NextSpinInfo } from '@shared/types/next-spin-info';
import { Spin } from '@shared/types/spin';
import { EngineService } from '@app/features/engine/engine.service';
import { isEngineWheel } from '@app/features/engine/engine-route';
import {
  Observable,
  BehaviorSubject,
  tap,
  throwError,
  catchError,
  map,
  timer,
} from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class SpinService {
  private http = inject(HttpClient);
  private engine = inject(EngineService);

  private _spinning = new BehaviorSubject<boolean>(false);
  private _spin = new BehaviorSubject<Spin | undefined>(undefined);
  private _spinInfo = new BehaviorSubject<NextSpinInfo | undefined>(undefined);
  private _showNextSpinInfo = new BehaviorSubject<boolean>(false);

  readonly spinning$ = this._spinning.asObservable();
  readonly spin$ = this._spin.asObservable();
  readonly spinInfo$ = this._spinInfo.asObservable();
  readonly showNextSpinInfo$ = this._showNextSpinInfo.asObservable();
  readonly allowedToSpin$ = this.spinInfo$.pipe(
    map((info) => !!info?.allowedToSpin),
  );

  updateSpinning(data: boolean): void {
    this._spinning.next(data);
  }

  getSpinningValue(): boolean {
    return this._spinning.getValue();
  }

  fetchSpinInfo(gameUuid: string): Observable<NextSpinInfo> {
    return this.http
      .get<NextSpinInfo>(environment.backendUrl + '/api/wheel/spin/' + gameUuid)
      .pipe(
        tap((data) => {
          if (data.nextSpinInSeconds > 0) {
            this.updateShowNextSpinInfo(true);
          }
          this.setSpinInfo({
            ...data,
            startTs: new Date().getTime(),
          });

          this.updateAllowedToSpinAfterDelay(data.nextSpinInSeconds);
        }),
      );
  }

  onSpin(gameUuid: string): Observable<Spin> {
    if (isEngineWheel()) {
      return this.engine.play().pipe(
        tap((spin) => {
          this.setSpin(spin);
          this.setSpinInfo(spin.nextSpinInfo);
        }),
      );
    }
    return this.http
      .post<Spin>(`${environment.backendUrl}/api/wheel/spin/${gameUuid}`, {})
      .pipe(
        tap((spin: Spin) => {
          this.setSpin(spin);
          this.setSpinInfo({
            ...spin.nextSpinInfo,
            lastSpinTransaction: {
              uuid: spin.uuid,
              game: spin.game,
              userInfo: spin.userInfo,
              createdTs: spin.createdTs,
              segment: spin.segment,
              nextSpinInfo: spin.nextSpinInfo,
            },
            startTs: new Date().getTime(),
          });
          this.updateAllowedToSpinAfterDelay(
            spin.nextSpinInfo.nextSpinInSeconds,
          );
        }),
        catchError((error: Error) => {
          return throwError(() => new Error(error.message));
        }),
      );
  }

  setSpinInfo(info: NextSpinInfo) {
    this._spinInfo.next(info);
  }

  updateShowNextSpinInfo(data: boolean) {
    this._showNextSpinInfo.next(data);
  }

  setSpin(data: Spin | undefined): void {
    this._spin.next(data);
  }

  private updateAllowedToSpinAfterDelay(seconds: number): void {
    if (seconds <= 0) {
      this.setSpinInfo({
        ...this._spinInfo.getValue()!,
        allowedToSpin: true,
      });
    } else {
      timer(seconds * 1000).subscribe(() => {
        const currentInfo = this._spinInfo.getValue();
        if (currentInfo) {
          this.setSpinInfo({
            ...currentInfo,
            allowedToSpin: true,
          });
        }
      });
    }
  }
}
